import { AfterContentInit, Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as _moment from 'moment';
import { default as _rollupMoment } from 'moment';
import { EuiDateRangeSelectorDates } from './models/eui-date-range-selector-dates.model';

const moment = _rollupMoment || _moment;

export const EUI_DATERANGESELECTOR_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => EuiDateRangeSelectorComponent),
    multi: true,
};

@Component({
    selector: 'eui-date-range-selector',
    templateUrl: './eui-date-range-selector.component.html',
    styleUrls: ['./styles/eui-date-range-selector.scss'],
    providers: [EUI_DATERANGESELECTOR_ACCESSOR],
    encapsulation: ViewEncapsulation.None,
})
export class EuiDateRangeSelectorComponent implements OnInit, AfterContentInit, OnDestroy, ControlValueAccessor {

    selectedDates: EuiDateRangeSelectorDates = new EuiDateRangeSelectorDates();
    private destroy$: Subject<boolean> = new Subject<boolean>();

    @Input() styleClass = '';
    @Input() e2eAttr = 'eui-date-range-selector';
    @Input() noButton = true;
    @Input() minDate: Date;
    @Input() maxDate: Date;
    @Input() togglerIcon = 'eui-icon-calendar';
    @Input() togglerLabel: string;
    @Input() firstInputPlaceholder: string;
    @Input() secondInputPlaceholder: string;
    @Input() startDateDefaultValue: Date;
    @Input() endDateDefaultValue: Date;
    @Input() comparisonStart: Date;
    @Input() comparisonEnd: Date;
    @Input() longDateFormat = false;
    @Input() disableButton = false;

    @Output() firstSelectedDate = new EventEmitter<Date>();
    @Output() secondSelectedDate = new EventEmitter<Date>();

    constructor(private translateService: TranslateService) {
    }

    ngOnInit() {
        if (!this.firstInputPlaceholder) {
            this.translateService.stream('eui.daterangeselector.FIRSTPICKERSLABEL').pipe(takeUntil(this.destroy$))
                .subscribe((result: string) => {
                    this.firstInputPlaceholder = result;
                });
        }
        if (!this.secondInputPlaceholder) {
            this.translateService.stream('eui.daterangeselector.SECONDPICKERSLABEL').pipe(takeUntil(this.destroy$))
                .subscribe((result: string) => {
                    this.secondInputPlaceholder = result;
                });
        }
    }

    ngAfterContentInit() {
        if (this.longDateFormat) {
            this.styleClass += 'eui-date-range-selector--long-date-format';
        }
    }

    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public onFirstDateChange(e: MatDatepickerInputEvent<Date>) {
        let value = moment(e.value);
        this.selectedDates.startRange = value.toDate();
        this.firstSelectedDate.emit(value.toDate());
        this.propagateChange(this.selectedDates);
    }

    public onSecondDateChange(e: MatDatepickerInputEvent<Date>) {
        let value = moment(e.value);
        this.selectedDates.endRange = value.toDate();
        this.secondSelectedDate.emit(value.toDate());
        this.propagateChange(this.selectedDates);
    }

    writeValue(values: EuiDateRangeSelectorDates): void {
        this.startDateDefaultValue = values?.startRange;
        this.endDateDefaultValue = values?.endRange;
        this.selectedDates = values;
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.propagateTouched = fn;
    }

    private propagateChange = (_: any) => {
    }

    private propagateTouched = () => {
    }
}
